import qs from 'qs';
import { authAPI } from '../../core/instance';
import { DateTime } from 'luxon';
import {
  StudentsExtras,
  StudentStatusEventRequest,
  StudentStatusEvent,
} from '@asset/type/userType';
import { getStudentInfo, StudentInformation } from '../dashboard';
import { StudentWithdrawnReasonCategory } from '@asset/enum/UserEnum';
import { CommonResponse } from '@apis/common/response';
import { Grade } from '@asset/enum/enum';
import { PageBasedPagination } from '@asset/type/common/response';
import { ExamResult } from '@asset/type/student';

export const getStudentActivitySummaryApi = async (
  startDateTime: DateTime,
  endDateTime: DateTime,
) => {
  let url = '/api/study-data/activity-summary/';
  const queryString = qs.stringify({
    startDateTime: startDateTime.toISO(),
    endDateTime: endDateTime.toISO(),
  });
  url += `?${queryString}`;
  const data = await authAPI
    .get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const getStudentsExtrasApi = async (queryParameters: {
  studentIds: number[];
}): Promise<{
  success: boolean;
  data: StudentsExtras[];
}> => {
  const queryString = qs.stringify(queryParameters, {
    encodeValuesOnly: true,
    arrayFormat: 'comma',
  });
  const url = `/api/students/status/?${queryString}`;
  const data = await authAPI
    .get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const getStudentsInfoByIds = async (
  studentIds: number[],
): Promise<(StudentInformation & { id: number })[]> => {
  const promises = studentIds.map(async (id) => {
    const studentInfo = await getStudentInfo(id);
    return {
      ...studentInfo,
      id: id,
    };
  });

  const studentInfosWithId = await Promise.all(promises);
  return studentInfosWithId;
};

export const getStudentStatusEvents = async (
  studentId: number,
): Promise<{ data: StudentStatusEvent[] }> => {
  const data = await authAPI
    .get(`/api/students/${studentId}/status/changes`)
    .then((response) => response.data)
    .catch((error) => error.response?.data ?? []);
  return data;
};

export const postStudentStatusEvent = async (
  studentId: number,
  requestBody: StudentStatusEventRequest,
): Promise<{ data: StudentStatusEvent }> => {
  const formattedRequestBody = {
    ...requestBody,
    changeDate: requestBody.changeDate.toFormat('yyyy-MM-dd'),
  };
  const data = await authAPI
    .post(`/api/students/${studentId}/status/changes`, formattedRequestBody)
    .then((response) => response.data)
    .catch((error) => error.response?.data ?? null);
  return data;
};

export const getStudentStatusEvent = async (
  eventId: number,
): Promise<{ data: StudentStatusEvent }> => {
  const data = await authAPI
    .get(`/api/students/status/changes/${eventId}`)
    .then((response) => response.data)
    .catch((error) => error.response?.data ?? null);
  return data;
};

export const patchStudentStatusEvent = async (
  eventId: number,
  requestBody: {
    changeDate?: DateTime;
    reason?: string;
    reasonCategory?: StudentWithdrawnReasonCategory;
  },
): Promise<{ data: StudentStatusEvent }> => {
  const formattedRequestBody = {
    ...requestBody,
    changeDate: requestBody.changeDate?.toFormat('yyyy-MM-dd'),
  };
  const data = await authAPI
    .patch(`/api/students/status/changes/${eventId}`, formattedRequestBody)
    .then((response) => response.data)
    .catch((error) => error.response?.data ?? null);
  return data;
};

export const deleteStudentStatusEvent = async (eventId: number) => {
  const result = await authAPI
    .delete(`/api/students/status/changes/${eventId}`)
    .then((response) => response.data)
    .catch((error) => error.response?.data ?? null);
  return result;
};

export const getStudentsByParentPhoneNumber = async (
  phoneNumber: string,
): Promise<{
  id: number;
  phoneNumber: string;
  students: Array<{
    id: number;
    name: string;
  }>;
}> => {
  const data = await authAPI
    .get(`/api/students/parents/by-phone-number/${phoneNumber}`)
    .then((response) => {
      return response.data;
    });
  return data;
};

export const getStudentExamResults = async (
  studentId: number,
  query: {
    page: number;
    pageSize: number;
  },
): Promise<
  CommonResponse & {
    data: {
      pagination: PageBasedPagination;
      results: ExamResult[];
    };
  }
> => {
  const data = await authAPI
    .get(
      `/api/students/${studentId}/exam-results?${qs.stringify(query, {
        skipNulls: true,
      })}`,
    )
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
  return data;
};

export const postStudentExamResult = async (
  studentId: number,
  requestBody: {
    title: string;
    examDate: DateTime;
    grade: Grade;
    score: string;
    memo?: string;
    schoolId?: number;
  },
): Promise<
  CommonResponse & {
    data: ExamResult;
  }
> => {
  const cleanedRequestBody = {
    ...requestBody,
    examDate: requestBody.examDate.toFormat('yyyy-MM-dd'),
  };
  const data = await authAPI
    .post(`/api/students/${studentId}/exam-results`, cleanedRequestBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
  return data;
};

export const postStudentExamResultFiles = async (
  examResultId: number,
  requestBody: FormData,
): Promise<
  CommonResponse & {
    data: {
      id: number;
      fileUrl: string;
    };
  }
> => {
  const data = await authAPI
    .post(`/api/students/exam-results/${examResultId}/files`, requestBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
  return data;
};

export const getStudentExamResult = async (
  id: number,
): Promise<
  CommonResponse & {
    data: ExamResult;
  }
> => {
  const data = await authAPI
    .get(`/api/students/exam-results/${id}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
  return data;
};

export const putStudentExamResult = async (
  id: number,
  requestBody: {
    title: string;
    examDate: DateTime;
    grade: Grade;
    score: string;
    memo?: string;
    schoolId?: number;
  },
): Promise<
  CommonResponse & {
    data: ExamResult;
  }
> => {
  const cleanedRequestBody = {
    ...requestBody,
    examDate: requestBody.examDate.toFormat('yyyy-MM-dd'),
  };
  const data = await authAPI
    .put(`/api/students/exam-results/${id}`, cleanedRequestBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
  return data;
};

export const patchStudentExamResult = async (
  id: number,
  requestBody: {
    title?: string;
    examDate?: DateTime;
    grade?: Grade;
    score?: string;
    memo?: string;
    schoolId?: number;
  },
): Promise<
  CommonResponse & {
    data: ExamResult;
  }
> => {
  const cleanedRequestBody = {
    ...requestBody,
    examDate: requestBody.examDate.toFormat('yyyy-MM-dd'),
  };
  const data = await authAPI
    .patch(`/api/students/exam-results/${id}`, cleanedRequestBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
  return data;
};

export const deleteStudentExamResult = async (id: number) => {
  const data = await authAPI
    .delete(`/api/students/exam-results/${id}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
  return data;
};

export const deleteStudentExamResultFiles = async (id: number) => {
  const data = await authAPI
    .delete(`/api/students/exam-results/files/${id}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
  return data;
};
